import React, { useMemo } from 'react';
import { ArtOfPossibleArea, ArtOfPossibleNote, ArtOfPossibleResponses } from '../../types';

interface ArtOfPossibleTabProps {
    aops?: ArtOfPossibleResponses;
}

const ArtOfPossibleTab: React.FC<ArtOfPossibleTabProps> = ({ aops = { aops: [] } }) => {
    const orderedAreas = useMemo(() => ([
        'Cost Optimization',
        'AI/ML',
        'RPA',
        'Automation',
        'Cloud',
        'Data Center',
        'Contact Center',
        'Collaboration',
        'Network',
        'Security',
    ]), []);

    const groupedStrategies = useMemo(() => {
        return aops.aops.reduce((acc: Record<string, ArtOfPossibleNote[]>, areaData: ArtOfPossibleArea) => {
            if (!acc[areaData.area]) {
                acc[areaData.area] = [];
            }
            const orderedNotes = [...areaData.notes].sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));

            acc[areaData.area].push(...orderedNotes);
            return acc;
        }, {} as Record<string, ArtOfPossibleNote[]>);
    }, [aops]);

    const areaStyles = useMemo<Record<string, {
        band: string;
        row: string;
        active: string;
        text: string;
    }>>(() => ({
        'Cost Optimization': {
            band: 'bg-[#87A2D3]',
            row: 'bg-[#D9E0F0]',
            active: 'bg-[#3B78B3]',
            text: 'text-slate-950',
        },
        'AI/ML': {
            band: 'bg-[#F4AE7A]',
            row: 'bg-[#FBE2D1]',
            active: 'bg-[#F2A46E]',
            text: 'text-slate-950',
        },
        RPA: {
            band: 'bg-[#F8D76C]',
            row: 'bg-[#FAEEBF]',
            active: 'bg-[#F7CF59]',
            text: 'text-slate-950',
        },
        Automation: {
            band: 'bg-[#A8CD8A]',
            row: 'bg-[#D8E4CC]',
            active: 'bg-[#93BD6E]',
            text: 'text-slate-950',
        },
        Cloud: {
            band: 'bg-[#B99CD7]',
            row: 'bg-[#E5D8EE]',
            active: 'bg-[#A678CF]',
            text: 'text-slate-950',
        },
        'Data Center': {
            band: 'bg-[#3E62A5]',
            row: 'bg-[#B7C4E1]',
            active: 'bg-[#2C5688]',
            text: 'text-white',
        },
        'Contact Center': {
            band: 'bg-[#D0610D]',
            row: 'bg-[#F7CDAE]',
            active: 'bg-[#C65A08]',
            text: 'text-white',
        },
        Collaboration: {
            band: 'bg-[#C79C00]',
            row: 'bg-[#F9E6A4]',
            active: 'bg-[#BC9600]',
            text: 'text-white',
        },
        Network: {
            band: 'bg-[#5A8B35]',
            row: 'bg-[#CCE1B8]',
            active: 'bg-[#4C7D2A]',
            text: 'text-white',
        },
        Security: {
            band: 'bg-[#9451C9]',
            row: 'bg-[#D8C1EA]',
            active: 'bg-[#8440BC]',
            text: 'text-white',
        },
    }), []);

    const fallbackAreaStyle = {
        band: 'bg-slate-500',
        row: 'bg-slate-100',
        active: 'bg-slate-600',
        text: 'text-white',
    };

    const matchesImpact = (value: string | undefined, target: 'high' | 'mid' | 'low') => {
        const normalized = value?.toLowerCase();
        return normalized === target || (normalized === 'medium' && target === 'mid');
    };

    return (
        <div className="px-4">
            <div className="mb-6">
                <h1 className="text-2xl font-bold italic text-[#0F2A4F]">Art Of Possible</h1>
                <p className="mt-2 max-w-4xl text-sm text-slate-600">
                    A matrix view of prioritized opportunity areas, with each idea plotted against business and technical impact to make trade-offs easier to discuss.
                </p>
            </div>

                <div className="overflow-x-auto rounded-2xl border border-slate-300 bg-white shadow-[0_18px_42px_rgba(15,42,79,0.09)]">
                    <table className="w-full min-w-[1080px] table-fixed border-collapse">
                        <thead>
                            <tr className="bg-[#2F5D8A] text-white">
                                <th rowSpan={2} className="w-[200px] border border-slate-900/80 px-4 py-3 text-left text-sm font-semibold uppercase tracking-wide">
                                    Transformation Area
                                </th>
                                <th rowSpan={2} className="w-[520px] border border-slate-900/80 px-4 py-3 text-center text-[18px] font-bold">
                                    Description
                                </th>
                                <th colSpan={3} className="border border-slate-900/80 px-2 py-2 text-center text-sm font-bold">
                                    Business Impact
                                </th>
                                <th colSpan={3} className="border border-slate-900/80 px-2 py-2 text-center text-sm font-bold">
                                    Technical Impact
                                </th>
                            </tr>
                            <tr className="bg-[#21486D] text-white">
                                {['High', 'Mid', 'Low', 'High', 'Mid', 'Low'].map((label, index) => (
                                    <th
                                        key={`${label}-${index}`}
                                        className="w-[48px] border border-slate-900/80 px-1 py-1.5 text-center text-sm font-semibold"
                                    >
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {orderedAreas.map((area) => {
                                const areaStrategies = groupedStrategies[area] ?? [];
                                const style = areaStyles[area] || fallbackAreaStyle;
                                const rows = areaStrategies.length > 0 ? areaStrategies : [null];

                                return (
                                    <React.Fragment key={area}>
                                        {rows.map((strategy, index) => (
                                            <tr key={`${area}-${index}`} className={`${style.row} border-y border-slate-900/80`}>
                                                {index === 0 && (
                                                    <td
                                                        rowSpan={rows.length}
                                                        className={`border border-slate-900/80 px-4 py-4 text-center text-[16px] font-bold ${style.band} ${style.text}`}
                                                    >
                                                        {area}
                                                    </td>
                                                )}

                                                <td className="border border-slate-900/80 px-4 py-1 text-[15px] leading-tight text-slate-950">
                                                    {strategy?.name ?? ''}
                                                </td>

                                                {(['high', 'mid', 'low'] as const).map((impact) => (
                                                    <td
                                                        key={`business-${area}-${index}-${impact}`}
                                                        className={`h-[40px] border border-slate-900/80 text-center text-xl font-bold ${
                                                            matchesImpact(strategy?.priority, impact) ? `${style.active} text-white` : ''
                                                        }`}
                                                    >
                                                        {matchesImpact(strategy?.priority, impact) ? 'X' : ''}
                                                    </td>
                                                ))}

                                                {(['high', 'mid', 'low'] as const).map((impact) => (
                                                    <td
                                                        key={`technical-${area}-${index}-${impact}`}
                                                        className={`h-[40px] border border-slate-900/80 text-center text-xl font-bold ${
                                                            matchesImpact(strategy?.growth, impact) ? `${style.active} text-white` : ''
                                                        }`}
                                                    >
                                                        {matchesImpact(strategy?.growth, impact) ? 'X' : ''}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
            </div>
        </div>
    );
};

export default ArtOfPossibleTab;
